import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { Routes } from '../../routes';
import { InputState, input } from '../../components/input';
import { isNonEmptyString } from '../../core';
import { getApiTeamError } from '../../api';
import { Error } from '../../core/error';
import { DEFAULT_GPT_QUESTION } from '../../constants/ai';

export class CliprGptSearchResultPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      teamId: observable,
      cliprGptSearchResult: observable,
      cliprGptSearchKey: observable,
      isFocused: observable,
      isMobileSidebarOpen: observable,
      setMobileSidebarOpen: action,
      isLoading: observable,
      error: observable,
      cliprGptError: observable,
      team: computed,
      user: computed,
      isDefaultQuestion: computed,
      isWidgetMode: computed,
      search: action,
      clear: action,
      mounted: action,
      unmounted: action,
      submit: action,
      searchUrl: computed,
    });

    this.model = input(this, {
      name: 'searchInput',
      onBlur: () => { setTimeout(action(() => { this.isFocused = false; }), 200) }
    });
  }

  readonly model: InputState;
  teamId: string | null = null;
  cliprGptSearchResult: string | null = null;
  cliprGptSearchKey: string | null = null;
  isFocused: boolean = false;
  isMobileSidebarOpen = false;
  setMobileSidebarOpen = (open: boolean) => {
    this.isMobileSidebarOpen = open;
  };
  isLoading: boolean = false;
  error: Error | null = null;
  cliprGptError: string | null = null;

  get team() {
    return this.store.teamManager.getTeam(this.teamId) ?? null;
  }
  get user() {
    return this.store.user ?? null;
  }
  get isDefaultQuestion() {
    return this.cliprGptSearchKey?.toLowerCase() === DEFAULT_GPT_QUESTION.toLowerCase();
  }

  get isWidgetMode(): boolean {
    return this.store.widget.isWidgetMode;
  }

  search = async() => {
    if (!this.cliprGptSearchKey || !isNonEmptyString(this.cliprGptSearchKey)) {
      return;
    }

    this.isLoading = true;

    const profile = this.store.user;
    const userId = !this.teamId ? profile?.id : undefined;
    const [res, err] = await this.store.api.getGPTAnswer({
      input: {
      question: this.cliprGptSearchKey!,
      teamId: this.teamId ?? undefined,
      userId: userId ?? undefined
      }
    });

    if (!res || err) {
      this.isLoading = false;
      this.cliprGptError =  err?.message ?? null;
      return;
    }

    this.cliprGptSearchResult = res.getGPTAnswer.result;
    this.cliprGptError =  null;

    this.model.clear();
    this.isLoading = false;
  }

  clear() {
    this.model.clear();
  }

  async mounted(teamId?: string | null) {
    const query = new URLSearchParams(window.location.search);
    const searchParam = query.get('searchKey');
    const teamIdParam = query.get('teamId');
    // Set the search value at init for shareable search urls
    this.model.value = searchParam || '';
    this.teamId = teamId || teamIdParam;
    this.cliprGptSearchKey = searchParam;

    if (this.teamId && !this.team) {
      const [, teamErr] = await this.store.teamManager.apiFetchTeam({ id: this.teamId });

      if (teamErr) {
        const decodedError = getApiTeamError(teamErr);
        this.error = decodedError;
        return;
      }
    }

    this.search();
  }

  unmounted() {
    this.model.clear();

    this.cliprGptSearchKey = null;
    this.teamId = null;
  }

  submit = (replaceRoute = false, teamId: string | null, value?: string | null) => {
    if (value)
      this.model.loadValue(value);

    if (!this.model.value || !isNonEmptyString(this.model.value)) {
      return;
    }

    this.teamId = teamId;
    this.cliprGptSearchKey = this.model.value;
    this.cliprGptSearchResult = null;
    this.isFocused = false;

    const { pathname } = window.location;
    if (this.isWidgetMode && pathname === Routes.librarySearchCliprGptWidget()) {
      this.search();
      return;
    }

    const { history } = this.store.routingService;
    if (replaceRoute)
      history.replace(this.searchUrl);
    else
      history.push(this.searchUrl);
  }

  get searchUrl(): string {
    const path = this.isWidgetMode ? Routes.librarySearchCliprGptWidget() : Routes.searchCliprGpt;

    const params = new URLSearchParams();
    if (this.model.value) {
      params.append('searchKey', this.model.value);
    }
    if (this.teamId) {
      params.append('teamId', this.teamId);
    }

    return `${path}${params ? `?${params}` : ''}`;
  }
}
